import React, { useState, useEffect, useContext } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  FormHelperText,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import Modal from 'components/_layouts/Modal';
import { FilterAlt } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import ManualPicker from 'components/_pickers/ManualPicker';

export type DTBFiltersParams = {
  search?: string | null;
  status?: string | null;
  limit?: string;
  linkName?: string;
  manualId?: string;
  createdDateStart?: string;
  createdDateEnd?: string;
  distributedDateStart?: string;
  distributedDateEnd?: string;
};

export type DTBFiltersProps = {
  params: DTBFiltersParams;
  updateParams: (updatedParams: DTBFiltersParams) => void;
};

const DTBFilters: React.FC<DTBFiltersProps> = ({ params, updateParams }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [showFilters, setShowFilters] = useState(false);
  const [manualId, setManualId] = useState<string>(params.manualId || '');
  const [status, setStatus] = useState<string>(params.status || 'ALL');

  const [createdDateRange, setCreatedDateRange] = useState<DateRange<Dayjs>>([
    params.createdDateStart ? dayjs(params.createdDateStart) : null,
    params.createdDateEnd ? dayjs(params.createdDateEnd) : null,
  ]);
  const [distributedDateRange, setDistributedDateRange] = useState<DateRange<Dayjs>>([
    params.distributedDateStart ? dayjs(params.distributedDateStart) : null,
    params.distributedDateEnd ? dayjs(params.distributedDateEnd) : null,
  ]);

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: t('common.dateRangeFilters.lastMonth'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('month').subtract(1, 'month');
        return [startOfLastMonth, startOfLastMonth.endOf('month')];
      },
    },
    {
      label: t('common.dateRangeFilters.lastQuarter'),
      getValue: () => {
        const today = dayjs();
        const startOfLastQuarter = today.startOf('quarter').subtract(1, 'quarter');
        return [startOfLastQuarter, startOfLastQuarter.endOf('quarter')];
      },
    },
    {
      label: t('common.dateRangeFilters.thisYear'),
      getValue: () => {
        const today = dayjs();
        const startOfYear = today.startOf('year');
        return [startOfYear, today];
      },
    },
    {
      label: t('common.dateRangeFilters.lastYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastYear = today.startOf('year').subtract(1, 'year');
        return [startOfLastYear, startOfLastYear.endOf('year')];
      },
    },
  ];

  useEffect(() => {
    setManualId(params.manualId || '');
    setStatus(params.status || 'ALL');
    setCreatedDateRange([
      params.createdDateStart ? dayjs(params.createdDateStart) : null,
      params.createdDateEnd ? dayjs(params.createdDateEnd) : null,
    ]);
    setDistributedDateRange([
      params.distributedDateStart ? dayjs(params.distributedDateStart) : null,
      params.distributedDateEnd ? dayjs(params.distributedDateEnd) : null,
    ]);
  }, [params]);

  const handleApplyFilters = () => {
    const newParams: DTBFiltersParams = {
      manualId: manualId || undefined,
      status: status || undefined,
    };

    if (createdDateRange && createdDateRange[0] && createdDateRange[1]) {
      if (createdDateRange[0].isAfter(createdDateRange[1])) {
        return;
      }
      newParams.createdDateStart = createdDateRange[0].format('YYYY-MM-DD');
      newParams.createdDateEnd = createdDateRange[1].format('YYYY-MM-DD');
    }

    if (distributedDateRange && distributedDateRange[0] && distributedDateRange[1]) {
      if (distributedDateRange[0].isAfter(distributedDateRange[1])) {
        return;
      }
      newParams.distributedDateStart = distributedDateRange[0].format('YYYY-MM-DD');
      newParams.distributedDateEnd = distributedDateRange[1].format('YYYY-MM-DD');
    }

    updateParams(newParams);
    setShowFilters(false);
  };

  return (
    <>
      <Button
        onClick={() => setShowFilters(true)}
        size='small'
        color='neutral'
        startIcon={<FilterAlt />}
        className='icon-only'
      />
      <Modal
        title={t('dtbs.library.filters.root')}
        size='sm'
        actions={
          <>
            <Button
              onClick={() => setShowFilters(false)}
              color='neutral'
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleApplyFilters}
            >
              {t('dtbs.library.filters.apply')}
            </Button>
          </>
        }
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Stack spacing={4}>
          <FormControl>
            <FormLabel>{t('dtbs.library.filters.status')}</FormLabel>
            <RadioGroup
              row
              value={status}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatus(e.target.value)}
            >
              <FormControlLabel
                value='ALL'
                control={<Radio />}
                label='All'
              />
              <FormControlLabel
                value='DRAFT'
                control={<Radio />}
                label='Draft'
              />
              <FormControlLabel
                value='APPROVED'
                control={<Radio />}
                label='Approved'
              />
            </RadioGroup>
          </FormControl>
          <ManualPicker
            id='manual-picker'
            label={t('dtbs.library.filters.manualName')}
            organizationId={currentOrganizationId || ''}
            value={manualId ? [manualId] : []}
            onChange={(newValue: string[]) => setManualId(newValue[0] || '')}
            placeholder={t('dtbs.library.filters.selectManual')}
          />
          <FormControl fullWidth>
            <FormLabel>{t('dtbs.library.filters.createdDate')}</FormLabel>
            <DateRangePicker
              value={createdDateRange}
              onChange={(newValue) => setCreatedDateRange(newValue)}
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                },
                actionBar: { actions: [] },
              }}
              sx={{
                '& .MuiFormControl-root': { paddingTop: 0 },
                '& .MuiFormLabel-root': { display: 'none' },
              }}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>{t('dtbs.library.filters.distributedDate')}</FormLabel>
            <DateRangePicker
              value={distributedDateRange}
              onChange={(newValue) => setDistributedDateRange(newValue)}
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                },
                actionBar: { actions: [] },
              }}
              sx={{
                '& .MuiFormControl-root': { paddingTop: 0 },
                '& .MuiFormLabel-root': { display: 'none' },
              }}
            />
          </FormControl>
        </Stack>
      </Modal>
    </>
  );
};

export default DTBFilters;
